// src/pages/Users/Messaging/MessagesReceived.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  markAllMessagesAsRead,
} from "../../services/messageServices";
import { useMessage } from "../../hooks/MessageContext";
import CreateResponseMessageForm from "./CreateResponseMessageForm";
import ButtonDelete from "../../components/Button/ButtonDelete";
import ButtonEyeNoRead from "../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../components/Button/ButtonEyeRead";
import ButtonReply from "../../components/Button/Buttonreply";
import { toast } from "react-toastify";
import { FaEnvelope, FaCheckDouble } from "react-icons/fa";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export default function MessagesReceived({
  userId,
  refresh,
  setRefresh,
  onCounts,
}) {
  const [messages, setMessages] = useState([]);
  const [replyMessageId, setReplyMessageId] = useState(null);
  const [busyAllRead, setBusyAllRead] = useState(false);
  const [loading, setLoading] = useState(false);

  const { setUnreadMessageCount } = useMessage();
  const navigate = useNavigate();

  // ----- REFS anti-boucle / stabilité -----
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Verrou de requête (évite les fetch concurrents ET évite d'ajouter `loading` en dépendance)
  const isFetchingRef = useRef(false);

  const onCountsRef = useRef(onCounts);
  useEffect(() => {
    onCountsRef.current = onCounts;
  }, [onCounts]);

  const setUnreadRef = useRef(setUnreadMessageCount);
  useEffect(() => {
    setUnreadRef.current = setUnreadMessageCount;
  }, [setUnreadMessageCount]);

  // Garde le dernier état envoyé pour ne pousser que si ça change
  const lastCountsRef = useRef({ total: null, unread: null });

  const computeUnread = (list) =>
    (list || []).reduce(
      (n, m) =>
        n + (!m.read_message || m.read_message === 0 || m.read_message === "0" ? 1 : 0),
      0
    );

  const pushCountsIfChanged = (list) => {
    const total = list.length;
    const unread = computeUnread(list);
    const prev = lastCountsRef.current;
    if (prev.total !== total || prev.unread !== unread) {
      lastCountsRef.current = { total, unread };
      onCountsRef.current?.({ total, unread });
      setUnreadRef.current?.(unread);
    }
  };

  const unread = computeUnread(messages);

  // ----- FETCH messages (sans dépendre de `loading`) -----
  const loadMessages = useCallback(
    async (reason = "load") => {
      if (!userId) return;
      if (isFetchingRef.current) return; // déjà en cours

      isFetchingRef.current = true;
      setLoading(true);
      try {
        const data = await getMessagesByUser(userId);
        const filtered = (data || []).filter((m) => !m.is_copy);
        if (!mountedRef.current) return;
        setMessages(filtered);
        pushCountsIfChanged(filtered);
      } catch (err) {
        console.error("[MessagesReceived] fetch error:", err);
        toast.error("Erreur lors du chargement des messages.");
      } finally {
        if (mountedRef.current) setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [userId] // ✅ uniquement userId → pas de recréation à chaque setState
  );

  // Au montage + quand userId change
  useEffect(() => {
    if (!userId) return;
    loadMessages("mount/userId");
  }, [userId, loadMessages]);

  // Consomme le refresh une seule fois, puis recharge
  useEffect(() => {
    if (!refresh) return;
    // Consommer le flag tout de suite pour éviter tout ping-pong
    setRefresh?.(false);
    loadMessages("refresh");
  }, [refresh, setRefresh, loadMessages]);

  // ----- Actions -----
  const handleReply = (message) => setReplyMessageId(message.id);
  const closeReplyForm = () => setReplyMessageId(null);

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessageById(messageId);
      const next = messages.filter((m) => m.id !== messageId);
      setMessages(next);
      pushCountsIfChanged(next);
      toast.success("Message supprimé avec succès.");
    } catch (err) {
      console.error("Erreur suppression message:", err);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  // Marquer comme lu seulement si nécessaire (pas d'appels inutiles)
  const handleMarkAsRead = async (messageId) => {
    try {
      const target = messages.find((m) => m.id === messageId);
      if (!target) return;
      const isUnread =
        !target.read_message || target.read_message === 0 || target.read_message === "0";
      if (!isUnread) return;

      await markMessageAsRead(messageId);
      const next = messages.map((m) =>
        m.id === messageId ? { ...m, read_message: true } : m
      );
      setMessages(next);
      pushCountsIfChanged(next);
    } catch (err) {
      console.error("Erreur marquage lu:", err);
      toast.error("Échec du marquage en lu.");
    }
  };

  // Option "non lu" locale (aucune API côté back)
  const handleMarkAsUnreadLocal = (messageId) => {
    const next = messages.map((m) =>
      m.id === messageId ? { ...m, read_message: false } : m
    );
    setMessages(next);
    pushCountsIfChanged(next);
  };

  const handleMarkAllAsRead = async () => {
    if (busyAllRead || messages.length === 0) return;
    try {
      setBusyAllRead(true);
      await markAllMessagesAsRead(userId);
      const next = messages.map((m) => ({ ...m, read_message: true }));
      setMessages(next);
      pushCountsIfChanged(next);
      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (err) {
      console.error("Erreur marquer tout lu:", err);
      toast.error("Échec du marquage global.");
    } finally {
      setBusyAllRead(false);
    }
  };

  const goToMessages = () => navigate("/messages");

  return (
    <div className="font-montserrat space-y-4">
      {/* Titre + boutons compacts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <div className="relative">
            <FaEnvelope className="text-xl" />
            {unread > 0 && (
              <span className="absolute -top-2 -right-4 bg-violet-600 text-white text-[10px] font-bold rounded-full px-1.5 py-[1px]">
                {unread}
              </span>
            )}
          </div>
          <span className="text-xl font-bold ml-4">
            {loading ? "Chargement…" : "Messages reçus"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            disabled={busyAllRead || messages.length === 0}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition disabled:opacity-50"
            title="Tout marquer comme lu"
          >
            <FaCheckDouble className="text-[12px]" />
            <span className="hidden sm:inline">Tout marquer lu</span>
          </button>
          {/* <button type="button" onClick={goToMessages} className="...">Nouveau / Voir</button> */}
        </div>
      </div>

      {/* Tableau desktop */}
      {messages.length > 0 ? (
        <>
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-slate-900/40 divide-y divide-slate-200 dark:divide-slate-700 rounded-xl overflow-hidden">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr className="text-slate-700 dark:text-slate-200">
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider dark:text-white">
                    Date
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider dark:text-white">
                    Détails
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider dark:text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {messages.map((message) => {
                  const isUnread =
                    !message.read_message ||
                    message.read_message === 0 ||
                    message.read_message === "0";
                  return (
                    <React.Fragment key={message.id}>
                      <tr
                        className={`${
                          isUnread
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : "bg-white dark:bg-transparent"
                        } hover:bg-slate-50 dark:hover:bg-slate-800/60 transition`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap align-top">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {fmtDate(message.created_at)}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            {fmtTime(message.created_at)}
                          </div>
                        </td>

                        <td className="px-3 py-2 align-top">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {message.Sender?.nom} {message.Sender?.prenom}
                          </div>
                          <div className="text-sm text-blue-700 dark:text-blue-300 font-bold">
                            {message.subject}
                          </div>
                          <div
                            className="prose prose-sm dark:prose-invert max-w-none mt-1 text-sm"
                            dangerouslySetInnerHTML={{
                              __html: message.content,
                            }}
                          />
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-right align-top">
                          <div className="flex items-center gap-2 justify-end">
                            <ButtonReply
                              onClick={() => setReplyMessageId(message.id)}
                              hoverLabel="Répondre"
                            />
                            {isUnread ? (
                              <ButtonEyeNoRead
                                onClick={() => handleMarkAsRead(message.id)}
                                hoverLabel="Marquer comme lu"
                              />
                            ) : (
                              <ButtonEyeRead
                                onClick={() => handleMarkAsUnreadLocal(message.id)}
                                hoverLabel="Marquer comme non lu (local)"
                              />
                            )}
                            <ButtonDelete
                              onClick={() => {
                                if (window.confirm("❗ Supprimer ce message ?")) {
                                  handleDeleteMessage(message.id);
                                }
                              }}
                              hoverLabel="Supprimer"
                            />
                          </div>
                        </td>
                      </tr>

                      {replyMessageId === message.id && (
                        <tr className="bg-slate-50 dark:bg-slate-800/60">
                          <td colSpan={3} className="px-3 py-3">
                            <CreateResponseMessageForm
                              show={true}
                              onHide={() => setReplyMessageId(null)}
                              userId={userId}
                              initialRecipientId={message.sender_id}
                              initialSubject={`RE: ${message.subject}`}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cartes mobile */}
          <div className="sm:hidden space-y-3">
            {messages.map((message) => {
              const isUnread =
                !message.read_message ||
                message.read_message === 0 ||
                message.read_message === "0";
              return (
                <div
                  key={message.id}
                  className={`rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 shadow-sm p-3 ${
                    isUnread
                      ? "bg-violet-50 dark:bg-violet-900/20"
                      : "bg-white dark:bg-slate-900/40"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {message.Sender?.nom} {message.Sender?.prenom}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400">
                        {fmtDate(message.created_at)} — {fmtTime(message.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <ButtonReply
                        onClick={() => setReplyMessageId(message.id)}
                        hoverLabel="Répondre"
                      />
                      {isUnread ? (
                        <ButtonEyeNoRead
                          onClick={() => handleMarkAsRead(message.id)}
                          hoverLabel="Marquer comme lu"
                        />
                      ) : (
                        <ButtonEyeRead
                          onClick={() => handleMarkAsUnreadLocal(message.id)}
                          hoverLabel="Marquer comme non lu (local)"
                        />
                      )}
                      <ButtonDelete
                        onClick={() => {
                          if (window.confirm("❗ Supprimer ce message ?")) {
                            handleDeleteMessage(message.id);
                          }
                        }}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </div>

                  <div className="text-sm text-blue-700 dark:text-blue-300 font-bold mt-1">
                    {message.subject}
                  </div>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none mt-1 text-sm"
                    dangerouslySetInnerHTML={{ __html: message.content }}
                  />

                  {replyMessageId === message.id && (
                    <div className="mt-2">
                      <CreateResponseMessageForm
                        show={true}
                        onHide={() => setReplyMessageId(null)}
                        userId={userId}
                        initialRecipientId={message.sender_id}
                        initialSubject={`RE: ${message.subject}`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-slate-600 dark:text-slate-300">
          {loading ? "Chargement…" : "Aucun message."}
        </div>
      )}
    </div>
  );
}
